import { AI_WORKFLOW_ERROR_MESSAGES } from './ai_workflow_errors'

export const MAX_SMART_PDF_FILE_SIZE_BYTES = 20 * 1024 * 1024
export const SMART_PDF_FILE_SIZE_ERROR_MESSAGE =
    'Die PDF-Datei ist größer als 20 MB. Bitte komprimieren Sie die Datei auf unter 20 MB und laden Sie sie erneut hoch.'
export const SMART_PDF_PROVIDER_FIT_ERROR_MESSAGE =
    'Die PDF-Datei konnte nicht für die KI-Verarbeitung vorbereitet werden. Bitte komprimieren Sie die Datei und versuchen Sie es erneut.'

const SMART_PDF_IMPORT_FAILURE_CODES = [
    'pdf_source_too_large',
    'pdf_page_limit_exceeded',
    'pdf_provider_fit_failed',
    'document_unreadable',
    'revaluation_report_type_mismatch',
    'model_output_max_tokens',
    'model_provider_unavailable',
    'async_invocation_timed_out',
    'async_invocation_interrupted',
    'external_valuation_tenant_summary_area_missing',
] as const

export type SmartPdfImportFailureCode = (typeof SMART_PDF_IMPORT_FAILURE_CODES)[number]

export const getSmartPdfImportFailureCode = (error: unknown): SmartPdfImportFailureCode | undefined => {
    if (!(error instanceof Error)) {
        return undefined
    }
    return SMART_PDF_IMPORT_FAILURE_CODES.find((code) => code === error.message)
}

const SMART_PDF_IMPORT_ERROR_MESSAGES: Record<SmartPdfImportFailureCode, string> = {
    ...AI_WORKFLOW_ERROR_MESSAGES,
    pdf_source_too_large: SMART_PDF_FILE_SIZE_ERROR_MESSAGE,
    pdf_page_limit_exceeded: 'Das PDF enthält zu viele Seiten. Bitte laden Sie ein kürzeres PDF hoch.',
    pdf_provider_fit_failed: SMART_PDF_PROVIDER_FIT_ERROR_MESSAGE,
    document_unreadable: 'Das PDF konnte nicht gelesen werden. Bitte prüfen Sie, ob es beschädigt oder passwortgeschützt ist.',
    revaluation_report_type_mismatch: 'Das PDF wurde nicht als EinWert-Folgebewertung erkannt.',
    model_output_max_tokens: 'Der Inhalt des PDFs war für einen vollständigen Import zu umfangreich. Bitte versuchen Sie es erneut.',
    external_valuation_tenant_summary_area_missing:
        'Ein Bewirtschaftungskostenansatz pro m² konnte ohne Bezugsfläche nicht verarbeitet werden. Bitte prüfen Sie das PDF.',
}

export const getSmartPdfImportErrorMessage = (error: unknown, fallback: string): string => {
    const code = getSmartPdfImportFailureCode(error)
    return code ? SMART_PDF_IMPORT_ERROR_MESSAGES[code] : fallback
}

const PDF_CONTENT_TYPE = 'application/pdf'

export const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
            const result = reader.result
            if (typeof result !== 'string' || !result.includes(',')) {
                reject(new Error('PDF konnte nicht gelesen werden.'))
                return
            }
            resolve(result.slice(result.indexOf(',') + 1))
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
    })

export const createAiWorkflowPdfFile = async (file: File) => ({
    name: file.name,
    source: {
        sourceType: 'BYTE_CONTENT' as const,
        byteContent: {
            mediaType: PDF_CONTENT_TYPE,
            data: await readFileAsBase64(file),
        },
    },
})
